import React, { useCallback, useMemo } from 'react'
import { PACKAGE_CHANGE_DOWNGRADE_TYPES } from 'constants/reservation'
import useOffer from 'hooks/Offers/useOffer'
import TextButton from 'components/Luxkit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import { useAppSelector } from 'hooks/reduxHooks'
import { OFFER_TYPE_TOUR } from 'constants/offer'
import { ChatActions } from 'api/support'
import { getCancellationConfig } from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/utils'
import {
  isDateChangeAllowed,
} from 'components/Common/OrderView/OrderItemSummaries/HotelItemSummary/HotelSummaryModifyBooking/utils'
import { OptimizelyServerSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import LesaHotelPurchaseItem from './LesaHotelPurchaseItem'

interface Props {
  order: App.Order;
  isPastOrder: boolean;
  hideSelect?: boolean;
  handleAction: (message: string, meta: any) => void;
}

export default function LesaHotelPurchases({ order, isPastOrder, hideSelect, handleAction }: Props) {
  const [offer]: any[] = useOffer<App.Offer>(order.items[0]?.offerId)
  let items = useMemo(() => order.items?.filter(i => !(i.status === 'cancelled' && PACKAGE_CHANGE_DOWNGRADE_TYPES.includes(i.downgradeType))), [order.items])

  // In case all items are cancelled, show the latest one
  if (items.length === 0) {
    items = order.items.slice(-1)
  }

  const item = items[0]

  const orderOffer = item?.offer
  const isTour = orderOffer?.parentType === OFFER_TYPE_TOUR

  const hasExperiences = !!order.experienceItems.length
  const hasAddons = !!order.addonItems.length
  const hasInsurance = !!order.insuranceItems.length
  const hasFlight = !!order.hasFlight

  const insuranceProductName = useAppSelector(state => state.geo.insuranceProductName)

  const lpcChangeDatesEnabled = !!useOptimizelyExperiment(OptimizelyServerSideExperiments.lpcChangeDatesEnabled)
  const cancellationPolicy = (item.reservation?.cancellationPolicies ?? [])[0]
  const numberOfDaysBeforeBookingIsLockedIn = offer?.daysBeforeCheckInChangesDisallowed || 0
  const changeDatesLimitReached = offer?.numberOfDateChangesAllowed !== 'Unlimited' && parseInt(offer?.numberOfDateChangesAllowed) <= item.numberOfDateChanges
  const { refundable, shouldHideFreeCancellation } = getCancellationConfig({ cancellationPolicy, offer, order, reservation: item.reservation as App.OrderReservation, hideCancellation: false })

  const canChangeDates = isDateChangeAllowed(false, item, item.reservation as App.OrderReservation, item.status === 'cancelled', order.hasFlight, numberOfDaysBeforeBookingIsLockedIn, changeDatesLimitReached, order.depositDetails, order.instalmentDetails, order.reserveForZeroDetails, shouldHideFreeCancellation, (hasExperiences || hasInsurance), lpcChangeDatesEnabled) && !item.isCompedPackage

  const handleOrderSelect = useCallback(() => {
    if (items.length === 1) {
      handleAction?.('', {
        key: ChatActions.SELECT_ORDER,
        payload: {
          id_orders: order.id,
          itemId: item.id,
          bookingNumber: item.bookingNumber,
          canBeCancelled: refundable && !shouldHideFreeCancellation,
          canChangeDates,
          checkInDate: item.reservation?.startDate,
          orderType: 'hotel',
        },
      })

      return
    }

    handleAction?.('', {
      key: ChatActions.SELECT_ORDER,
      payload: { id_orders: order.id, orderType: 'hotel' },
    })
  }, [canChangeDates, handleAction, item.bookingNumber, item.id, item.reservation?.startDate, items.length, order.id, refundable, shouldHideFreeCancellation])

  if (!offer) {
    return <LesaPurchaseSummarySkeleton />
  }

  return (
    <LesaPurchaseSummaryContainer>
      <LesaPurchaseSummaryHeader>
        <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineBedIcon size="XS"/>}>
          {isTour ? 'Tour' : 'Hotel'}
          {hasFlight && ' + Flights'}
          {(hasExperiences || hasAddons) && ' + Experiences'}
          {hasInsurance && ` + Travel ${insuranceProductName} - ${order.insuranceItems[0]?.productName}`}
        </BodyTextBlock>
        {!isPastOrder && <StatusLabel variant="confirmation">Upcoming</StatusLabel>}
        {isPastOrder && <StatusLabel variant="pending">Past</StatusLabel>}
      </LesaPurchaseSummaryHeader>
      <Group direction="vertical">
        <Group direction="vertical" gap={4}>
          {orderOffer?.location && <CaptionTextBlock
                variant="medium"
                colour="neutral-three"
              >{orderOffer.location}</CaptionTextBlock>}
          <BodyTextBlock lineClamp={2} weight="bold" variant="medium">{orderOffer?.name}</BodyTextBlock>{' '}
          {items.map(item => <LesaHotelPurchaseItem key={item.id} item={item} order={order} isTour={isTour} />)}
        </Group>
      </Group>
      {!hideSelect && <Group fullWidth direction="horizontal" horizontalAlign="end" verticalAlign="end">
        <TextButton onClick={handleOrderSelect} fit="mobile-full-width" size="small" kind="secondary">
          Select
        </TextButton>
      </Group>}
    </LesaPurchaseSummaryContainer>
  )
}
